import type { TaskLog } from "@/contracts/tasks";

export const TASK_LOG_ROW_HEIGHT = 30;
export const TASK_LOG_OVERSCAN_ROWS = 12;

export type LogLevelFilter = "all" | TaskLog["level"];
export type DisplayRow = {
  key: string;
  line: string;
  lineIndex: number;
  log: TaskLog;
};
export type LogLevelCounts = Record<TaskLog["level"], number>;

const logTimestampFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export function formatLogTimestamp(timestamp: number): string {
  const parts = Object.fromEntries(
    logTimestampFormatter
      .formatToParts(new Date(timestamp * 1000))
      .map(({ type, value }) => [type, value]),
  );

  return `${parts.month.toUpperCase()} ${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

export function countLogLevels(logs: readonly TaskLog[]): LogLevelCounts {
  return logs.reduce<LogLevelCounts>(
    (counts, log) => {
      counts[log.level] += 1;
      return counts;
    },
    { Information: 0, Warning: 0, Error: 0 },
  );
}

export function filterLogs(
  logs: readonly TaskLog[],
  level: LogLevelFilter,
  query: string,
): TaskLog[] {
  const normalizedQuery = query.toLocaleLowerCase();
  return logs.filter((log) => {
    if (level !== "all" && log.level !== level) return false;
    return (
      !normalizedQuery ||
      log.message.toLocaleLowerCase().includes(normalizedQuery) ||
      log.exception?.toLocaleLowerCase().includes(normalizedQuery) ||
      formatLogTimestamp(log.timestamp).toLocaleLowerCase().includes(normalizedQuery)
    );
  });
}

export function createDisplayRows(logs: readonly TaskLog[]): DisplayRow[] {
  // Reverse entries before splitting messages so multiline entries retain line order.
  return logs.toReversed().flatMap((log) =>
    formatLogMessage(log).split(/\r?\n/).map((line, lineIndex) => ({
      key: `${log.id}-${lineIndex}`,
      line,
      lineIndex,
      log,
    })),
  );
}

export function formatLogs(logs: readonly TaskLog[]): string {
  return logs.map((log) => `${formatLogTimestamp(log.timestamp)}  [${log.level}] ${formatLogMessage(log)}`).join("\n");
}

function formatLogMessage(log: Pick<TaskLog, "message" | "exception">): string {
  return log.exception ? `${log.message}\nException: ${log.exception}` : log.message;
}

export function createLogFilename(taskId: string): string {
  const name = taskId.replace(/[^a-z0-9-_]+/gi, "-").replace(/^-+|-+$/g, "");
  return `${name || "task"}-logs.txt`;
}

export function calculateVisibleRange(
  rowCount: number,
  scrollTop: number,
  viewportHeight: number,
  rowHeight = TASK_LOG_ROW_HEIGHT,
  overscanRows = TASK_LOG_OVERSCAN_ROWS,
) {
  return {
    startIndex: Math.max(0, Math.floor(scrollTop / rowHeight) - overscanRows),
    endIndex: Math.min(
      rowCount,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscanRows,
    ),
  };
}

export function calculateAnchoredScrollTop(
  previousRows: readonly DisplayRow[],
  nextRows: readonly DisplayRow[],
  previousScrollTop: number,
  rowHeight = TASK_LOG_ROW_HEIGHT,
): number | undefined {
  if (previousRows.length === 0) return undefined;

  const previousIndex = Math.floor(previousScrollTop / rowHeight);
  const offsetWithinRow = previousScrollTop % rowHeight;
  const anchorKey = previousRows[previousIndex]?.key;
  const nextIndex = anchorKey ? nextRows.findIndex((row) => row.key === anchorKey) : -1;
  return nextIndex >= 0 ? nextIndex * rowHeight + offsetWithinRow : undefined;
}

export function isNearLatest(scrollTop: number, rowHeight = TASK_LOG_ROW_HEIGHT): boolean {
  return scrollTop <= rowHeight * 2;
}
